// ContactListModal.jsx

import React, { useCallback, useEffect, useMemo, useState } from "react";
import Axios from "axios";
import {
    Box,
    Button,
    CircularProgress,
    Divider,
    Drawer,
    IconButton,
    InputAdornment,
    List,
    ListItem,
    ListItemText,
    Stack,
    TextField,
    Typography,
} from "@mui/material";

import { GBH_ID, SanitizeHotelID, URLAPI } from "../configuration";
import Icons from "../helpers/icons";

const drawerWidth = 600;

const ContactListDrawer = ({
    open,
    onClose,
    hotelID,
    auth,
    currentContacts,
    onSaved,
}) => {
    /* ----------------------------- state ---------------------------- */
    const [users, setUsers] = useState([]); // all hotel users
    const [selected, setSelected] = useState(new Set(currentContacts));
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState("");

    /* --------------------------- memo & fx -------------------------- */
    const headers = useMemo(
        () => ({ Authorization: `Bearer ${auth.user.jwt}` }),
        [auth],
    );

    const fetchUsers = useCallback(() => {
        if (!hotelID || !open) return;
        setLoading(true);
        setError(null);

        Axios.get(`${URLAPI}/hotels/users`, {
            params: { hotel_id: SanitizeHotelID(hotelID, GBH_ID) },
            headers,
        })
            .then((res) => {
                const uniqueUsers = Array.from(
                    new Map(
                        res.data.data.map((u) => [u.AccountID, u]),
                    ).values(),
                );

                setUsers(uniqueUsers);
                setSelected(new Set(currentContacts));
            })
            .catch((err) => setError(err.message))
            .finally(() => setLoading(false));
    }, [hotelID, headers, open, currentContacts]);

    useEffect(fetchUsers, [fetchUsers]);

    /* -------------------------- helpers ----------------------------- */
    const compareUsers = useCallback((a, b) => {
        const emailA = (a.EmailAddress || "").trim();
        const emailB = (b.EmailAddress || "").trim();
        const nameA = (a.Name || "").trim();
        const nameB = (b.Name || "").trim();

        // Primary key: name or email when name is missing
        const keyA = nameA || emailA;
        const keyB = nameB || emailB;

        const primary = keyA.localeCompare(keyB, undefined, {
            sensitivity: "base",
            numeric: true,
        });
        if (primary !== 0) return primary;

        // Tie‑breaker to keep deterministic order
        return emailA.localeCompare(emailB, undefined, {
            sensitivity: "base",
            numeric: true,
        });
    }, []);

    const toggle = (id) => {
        setSelected((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const hasChanges = useMemo(() => {
        if (selected.size !== currentContacts.length) return true;
        for (const id of currentContacts) if (!selected.has(id)) return true;
        return false;
    }, [selected, currentContacts]);

    // Filter helpers
    const selectedUsers = useMemo(
        () => users.filter((u) => selected.has(u.AccountID)).sort(compareUsers),
        [users, selected, compareUsers],
    );

    const unselectedFiltered = useMemo(() => {
        const term = search.toLowerCase();
        return users
            .filter(
                (u) =>
                    !selected.has(u.AccountID) &&
                    (`${u.Name} ${u.EmailAddress}`
                        .toLowerCase()
                        .includes(term) ||
                        term === ""),
            )
            .sort(compareUsers);
    }, [users, selected, search]);

    /* ---------------------------- save ------------------------------ */
    const handleSave = async () => {
        if (!hasChanges) {
            onClose();
            return;
        }
        setSaving(true);

        const toAdd = [];
        const toRemove = [];
        users.forEach((u) => {
            const nowSelected = selected.has(u.AccountID);
            const wasSelected = currentContacts.includes(u.AccountID);
            if (nowSelected && !wasSelected) toAdd.push(u.AccountID);
            if (!nowSelected && wasSelected) toRemove.push(u.AccountID);
        });

        try {
            await Promise.all([
                ...toAdd.map((id) =>
                    Axios.post(
                        `${URLAPI}/hotels/contacts`,
                        {
                            hotel_id: SanitizeHotelID(hotelID, GBH_ID),
                            account_id: id,
                        },
                        { headers },
                    ),
                ),
                ...toRemove.map((id) =>
                    Axios.delete(`${URLAPI}/hotels/contacts`, {
                        params: {
                            hotel_id: SanitizeHotelID(hotelID, GBH_ID),
                            account_id: id,
                        },
                        headers,
                    }),
                ),
            ]);
            onSaved();
        } catch (err) {
            setError(err.message);
            setSaving(false);
        }
    };

    /* ------------------------ close handler ------------------------- */
    const handleRequestClose = () => {
        if (hasChanges) {
            const confirm = window.confirm(
                "There are unsaved changes. Do you want to discard them?",
            );
            if (!confirm) return; // keep drawer open
        }
        onClose();
    };

    /* -------------------------- UI render --------------------------- */
    return (
        <Drawer anchor="right" open={open} onClose={handleRequestClose}>
            <Box
                sx={{
                    p: 6,
                    width: drawerWidth,
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                }}
            >
                {/* Header */}
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <Typography variant="h5">Edit contact list</Typography>
                    <IconButton onClick={handleRequestClose}>
                        <Icons.CloseNavbar />
                    </IconButton>
                </Stack>
                <Divider sx={{ my: 2 }} />

                {/* Content area – scrollable */}
                <Box sx={{ flexGrow: 1, overflow: "auto" }}>
                    {loading ? (
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                                my: 4,
                            }}
                        >
                            <CircularProgress />
                        </Box>
                    ) : error ? (
                        <Typography color="error">{error}</Typography>
                    ) : users.length === 0 ? (
                        <Typography>No users found for this hotel.</Typography>
                    ) : (
                        <>
                            {/* Selected contacts */}
                            <Typography fontSize="0.8em">
                                Selected contacts
                            </Typography>
                            <List sx={{ ml: 1, mr: 1 }}>
                                {selectedUsers.length === 0 && (
                                    <Typography
                                        sx={{ ml: 2, my: 1 }}
                                        fontSize="0.8em"
                                    >
                                        No contacts selected.
                                    </Typography>
                                )}
                                {selectedUsers.map((u) => (
                                    <ListItem
                                        key={u.AccountID}
                                        sx={{
                                            border: 1,
                                            borderColor: "grey.400",
                                            borderRadius: 1,
                                            mb: 1,
                                        }}
                                        secondaryAction={
                                            <IconButton
                                                edge="end"
                                                onClick={() =>
                                                    toggle(u.AccountID)
                                                }
                                            >
                                                <Icons.Remove />
                                            </IconButton>
                                        }
                                    >
                                        <ListItemText
                                            primary={u.Name}
                                            secondary={u.EmailAddress}
                                        />
                                    </ListItem>
                                ))}
                            </List>

                            {/* Search */}
                            <TextField
                                fullWidth
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                label="Search contacts"
                                placeholder="Type to search..."
                                sx={{ my: 2 }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Icons.Search />
                                        </InputAdornment>
                                    ),
                                }}
                            />

                            {/* Other contacts */}
                            <Typography fontSize="0.8em" sx={{ mt: 2 }}>
                                Other contacts
                            </Typography>
                            <List sx={{ ml: 1, mr: 1 }}>
                                {unselectedFiltered.length === 0 && (
                                    <Typography
                                        sx={{ ml: 2, my: 1 }}
                                        fontSize="0.8em"
                                    >
                                        No contacts match the search.
                                    </Typography>
                                )}
                                {unselectedFiltered.map((u) => (
                                    <ListItem
                                        key={u.AccountID}
                                        sx={{
                                            border: 1,
                                            borderColor: "grey.300",
                                            borderRadius: 1,
                                            mb: 1,
                                            // cursor: "pointer",
                                        }}
                                        // onClick={() => toggle(u.AccountID)}
                                        secondaryAction={
                                            <IconButton
                                                edge="end"
                                                onClick={() =>
                                                    toggle(u.AccountID)
                                                }
                                            >
                                                <Icons.Add />
                                            </IconButton>
                                        }
                                    >
                                        <ListItemText
                                            primary={u.Name}
                                            secondary={u.EmailAddress}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </>
                    )}
                </Box>

                {/* Footer buttons */}
                <Divider sx={{ mt: 1, mb: 2 }} />
                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                    <Button
                        onClick={handleRequestClose}
                        disabled={saving}
                        sx={{ mr: 2 }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={!hasChanges || saving}
                    >
                        {saving ? "Saving…" : "Save"}
                    </Button>
                </Box>
            </Box>
        </Drawer>
    );
};

export default ContactListDrawer;
